import React, { useMemo, useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useReactiveVar, useSubscription } from '@apollo/client';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { PAYMENT_TYPES_QUERY } from '../queries';
import { INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { SAVE_POLICY_DATA, GENERATE_POLICY } from '../mutations';
import { POLICY_GENERATED, POLICY_FILES_UPLOADED } from '../subscriptions';
import { POLICY_DATA_SAVED, OFFER_QUOTATION_RESET } from 'features/insuranceDocument/edit/root/subscriptions';
import { get } from '@totalsoft/change-tracking-react';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { shape, required, fromModel } from '@totalsoft/pure-validations';
import PolicyDataCard from './PolicyDataCard';
import { isDirty, isPropertyDirty } from '@totalsoft/change-tracking';
import { useDictionaryHandling, useToast, useParams, useSimpleError, useOverRouterContextUtils, useAuthorization } from 'hooks';
import { generationInProgressStatusVar } from 'features/insuranceDocument/variables';
import { useFileDownload } from 'hooks/fileHandling';
import { Prompt } from 'react-router-dom';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';

const policyValidator = fromModel(() =>
  shape({
    paymentTypeId: required,
    paymentDocumentSeries: required,
    paymentDocumentNumber: required,
    paymentDocumentDate: required
  })
);

const PolicyDataContainer = ({
  policyDataLens,
  policyDataLoading,
  policyDirtyInfo,
  resetPolicyLens,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  contextRef
}) => {
  const { documentTypeId, documentStateId, documentAllowedTransitions } = useContext(InsuranceDataContext);
  const hasRightToViewUploadDocuments = useAuthorization(AuthorizationCodes.hasRightToViewUploadDocuments, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const showError = useSimpleError();
  const addToast = useToast();
  const { setEvents } = useOverRouterContextUtils(contextRef);

  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [generateDisabled, setGenerateDisabled] = useState(false);
  const [downloadDisabled, setDownloadDisabled] = useState(true);
  const generationInProgress = useReactiveVar(generationInProgressStatusVar);

  const [validation, validate, resetValidation] = useDirtyFieldValidation(policyValidator);
  const { loading: insuranceTypesLoading, getCodeById: getInsuranceTypeCodeById } = useDictionaryHandling(
    INSURANCE_TYPES_QUERY,
    'insuranceTypes'
  );
  const insuranceTypeCode = useMemo(
    () => getInsuranceTypeCodeById(policyDataLens.insuranceTypeId |> get),
    [getInsuranceTypeCodeById, policyDataLens.insuranceTypeId]
  );

  setEvents({
    onChangeDocumentStateFailed: [
      ...contextRef.current.events.onChangeDocumentStateFailed,
      () => {
        setGenerateDisabled(false);
        setGenerating(false);
      }
    ],
    onPolicyGenerationError: () => {
      setGenerating(false);
    }
  });

  useEffect(() => {
    setDownloadDisabled((policyDataLens.policyFileUniqueId |> get) === null || !hasRightToViewUploadDocuments);
  }, [policyDataLens.policyFileUniqueId, hasRightToViewUploadDocuments]);

  const { loading: paymentTypesLoading, dictionary: paymentTypesData } = useDictionaryHandling(PAYMENT_TYPES_QUERY, 'paymentTypes');

  const [savePolicyData] = useMutation(SAVE_POLICY_DATA, {
    onError: error => {
      showError(error);
      setSaveDisabled(false);
      setSaving(false);
    }
  });

  const [generatePolicy] = useMutation(GENERATE_POLICY, {
    onError: error => showError(error),
    onCompleted: data => {
      const { correlationId } = data.generatePolicy;
      const { generatePolicyErrorHandling } = contextRef.current.functions;
      generatePolicyErrorHandling && generatePolicyErrorHandling({ correlationId });
    }
  });

  useEffect(() => {
    validate(policyDataLens |> get, policyDirtyInfo);
  }, [policyDataLens, policyDirtyInfo, validate]);

  useEffect(() => {
    setSaveDisabled(saving || !isDirty(policyDirtyInfo));
    setGenerateDisabled(
      generating ||
        isPropertyDirty('paymentDocumentNumber', policyDirtyInfo) ||
        isPropertyDirty('paymentDocumentSeries', policyDirtyInfo) ||
        isPropertyDirty('paymentDocumentDate', policyDirtyInfo) ||
        isPropertyDirty('paymentTypeId', policyDirtyInfo) ||
        isPropertyDirty('clientSelectionConfirmedByUser', policyDirtyInfo) ||
        ((policyDataLens.clientSelectionConfirmedByUser |> get) !== true && insuranceTypeCode === rcaInsuranceTypeCode)
    );
  }, [generating, policyDirtyInfo, saving, policyDataLens.clientSelectionConfirmedByUser, insuranceTypeCode, rcaInsuranceTypeCode]);

  useEffect(() => {
    setGenerating(generationInProgress);
  }, [generationInProgress]);

  const handleSave = useCallback(() => {
    if (!validate(policyDataLens |> get)) {
      return;
    }
    setSaving(true);
    savePolicyData({
      variables: {
        input: {
          documentId: policyDataLens.documentId |> get,
          paymentTypeId: policyDataLens.paymentTypeId |> get,
          paymentDocumentSeries: policyDataLens.paymentDocumentSeries |> get,
          paymentDocumentNumber: policyDataLens.paymentDocumentNumber |> get,
          paymentDocumentDate: policyDataLens.paymentDocumentDate |> get,
          otherInfo: policyDataLens?.otherInfo |> get,
          clientSelectionConfirmedByUser: policyDataLens.clientSelectionConfirmedByUser |> get
        }
      }
    });
  }, [policyDataLens, savePolicyData, validate]);

  const handleGenerate = useCallback(() => {
    if (!validate(policyDataLens |> get)) {
      return;
    }

    const offerDetailId = policyDataLens.offerDetailId |> get;
    const quotationId = policyDataLens.quotationId |> get;
    if ((!offerDetailId && insuranceTypeCode === rcaInsuranceTypeCode) || (!quotationId && insuranceTypeCode === cascoInsuranceTypeCode)) {
      addToast(t('Offer.Edit.PolicyData.QuotationSelection'), 'error', { toastId: 'quotationSelectedToast' });
      return;
    }
    setGenerating(true);
    const documentId = policyDataLens.documentId |> get;
    const input = { documentId };
    generatePolicy({
      variables: {
        input
      }
    });
  }, [addToast, generatePolicy, insuranceTypeCode, policyDataLens, t, validate, rcaInsuranceTypeCode, cascoInsuranceTypeCode]);

  const [downloadFile] = useFileDownload();

  const handleDownload = async () => {
    const policyFileUniqueId = policyDataLens.policyFileUniqueId |> get;
    if (!policyFileUniqueId) return;
    downloadFile(policyFileUniqueId);
  };

  useSubscription(POLICY_DATA_SAVED, {
    variables: { documentId: policyDataLens.documentId |> get },
    skip: !(policyDataLens.documentId |> get),
    onData: ({ data: { data } }) => {
      resetValidation();
      if (data.policyDataSaved.policyData) {
        resetPolicyLens(data.policyDataSaved.policyData);
      }
      const { silentMode } = data.policyDataSaved;
      if (!silentMode) addToast(t('General.Saved'), 'success', { toastId: 'policyDataSavedToast' });
      setSaving(false);
    }
  });

  useConditionalSubscription(POLICY_GENERATED, {
    variables: { documentId: policyDataLens.documentId |> get },
    skip: !(policyDataLens.documentId |> get),
    onData: ({ data: { data } }) => {
      setGenerating(false);

      const policyData = data.policyGenerated.policyData;
      if (policyData && policyData.insuranceDocumentNumber) {
        resetPolicyLens(policyData);
        addToast(t('Offer.Edit.PolicyData.Generated', { insuranceNumber: policyData.insuranceDocumentNumber }), 'success', {
          toastId: 'policyGeneratedToast'
        });
      } else {
        addToast(t('General.UnexpectedError'), 'error');
      }
    }
  });

  useConditionalSubscription(POLICY_FILES_UPLOADED, {
    onData: ({ data: { data } }) => {
      if (data.policyFileUploaded.policyData) {
        resetPolicyLens(data.policyFileUploaded.policyData);
        addToast(t('Offer.Edit.PolicyData.Uploaded'), 'success', { toastId: 'policyFilesUploadedToast' });
      }
    }
  });

  useConditionalSubscription(OFFER_QUOTATION_RESET, {
    onData: _ => {
      addToast(t('Offer.Edit.PolicyData.QuotationReset'), 'warning', { toastId: 'offerQuotationResetToast' });
    }
  });

  const offerValidationDocumentStateCode = useParams('OfferValidationDocumentStateCode');

  const generateHidden = () => {
    if (!offerValidationDocumentStateCode) return;
    if (documentAllowedTransitions.find(x => x.code === offerValidationDocumentStateCode) != null) return false;
    else return true;
  };

  return (
    <>
      <Prompt when={isDirty(policyDirtyInfo)} message={() => t('LeavingWithoutSaving')} />
      <PolicyDataCard
        loading={insuranceTypesLoading || paymentTypesLoading || policyDataLoading || !insuranceTypeCode}
        policyDataLens={policyDataLens}
        paymentTypes={paymentTypesData}
        insuranceTypeCode={insuranceTypeCode}
        rcaInsuranceTypeCode={rcaInsuranceTypeCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeCode}
        onSave={handleSave}
        onGenerate={handleGenerate}
        onDownload={handleDownload}
        saving={saving}
        generating={generating}
        saveDisabled={saveDisabled}
        generateDisabled={generateDisabled}
        generateHidden={generateHidden}
        validation={validation}
        downloadDisabled={downloadDisabled}
        readOnly={insuranceTypeCode === rcaInsuranceTypeCode && (policyDataLens?.negotiatedQuotation |> get)}
      />
    </>
  );
};

PolicyDataContainer.propTypes = {
  policyDataLens: PropTypes.object.isRequired,
  policyDataLoading: PropTypes.bool.isRequired,
  policyDirtyInfo: PropTypes.object.isRequired,
  resetPolicyLens: PropTypes.func.isRequired,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  contextRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

export default PolicyDataContainer;
